// ChartPump.jsx
import React from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import { injectIntl } from 'react-intl'
import Spinner from '../../../util/spinner-shim'
import _maxBy from 'lodash/maxBy'

import {
  chartDimensions,
  fourToTwenty,
} from 'altrac-conversions'

import NoChartData from './NoChartData'
import styles from '../../../../css/charts.module.css'

const formatTooltipValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 'N/A'
    try {
      return value.toLocaleString()
    } catch {
      // Some devices/browsers ship with a malformed default Intl locale,
      // which throws "failed to initialize DateTimeFormat" on toLocaleString().
      return String(value)
    }
  }
  return value
}

const formatTooltipDate = (timestamp) => {
  const date = new Date(timestamp)
  try {
    return date.toLocaleString()
  } catch {
    // Some devices/browsers ship with a malformed default Intl locale,
    // which throws "failed to initialize DateTimeFormat" on toLocaleString().
    return date.toISOString()
  }
}

const buildTooltip = (timestamp, entries = []) => {
  const dateString = formatTooltipDate(timestamp)
  const rowsMarkup = entries.length
    ? entries.map(({ label, value }) => `
        <div class="${styles.chartTooltipRow}">
          <span class="${styles.chartTooltipLabel}">${label}</span>
          <span class="${styles.chartTooltipValue}">${value}</span>
        </div>
      `).join('')
    : `<div class="${styles.chartTooltipEmpty}">No data</div>`

  return `
    <div class="${styles.chartTooltip}">
      <div class="${styles.chartTooltipHeader}">${dateString}</div>
      <div class="${styles.chartTooltipBody}">
        ${rowsMarkup}
      </div>
    </div>
  `
}

export const makeColumns = function makeColumns (
  translatedProps = [
    {
      type: 'date',
      label: 'Time',
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: 'Relay Output',
      role: 'data',
    },
    {
      type: 'number',
      label: 'Run Signal',
      role: 'data',
    },
    {
      type: 'number',
      label: 'Pressure (PSI)',
      role: 'data',
    },
  ]
) {
  return ([
    ...translatedProps,
  ])
}

export const makeRows = function makeRows (
  sensorData,
  device,
  translatedProps = {
    relay: 'Relay Output',
    run: 'Run Signal',
    pressure: 'Pressure (PSI)',
  }
) {
  const rows = []
  if (
    sensorData.length > 0
  ) {
    for (let i = 0; i < sensorData.length; i += 1) {
      const runSignal = sensorData[i]['134']
      const relayOutput = sensorData[i]['131']
      const { physical } = device
      let pressure = fourToTwenty(
        sensorData[i]['133'],
        physical.min || 0,
        physical.max || 100,
        physical.zero || 0,
        1
      )
      if (pressure === 'OFF' || pressure === 'ERH') {
        pressure = 0
      }
      const combined = buildTooltip(sensorData[i].date, [
        {
          label: translatedProps.run,
          value: formatTooltipValue(runSignal),
        },
        {
          label: translatedProps.relay,
          value: formatTooltipValue(relayOutput),
        },
        {
          label: translatedProps.pressure,
          value: formatTooltipValue(pressure),
        },
      ])
      if (
        (runSignal || runSignal === 0) ||
        (relayOutput || relayOutput === 0) ||
        pressure
      ) {
        rows.push(
          [
            new Date(sensorData[i].date),
            combined,
            relayOutput,
            runSignal,
            pressure,
          ]
        )
      }
    }
  }
  return rows
}

function ChartPump ({
  sensorData,
  metricReady,
  device,
  intl: {
    formatMessage,
  }
}) {
  if (!metricReady) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const rows = makeRows(
    sensorData,
    device,
    {
      relay: formatMessage({
        id: 'sensor.control.relayOutput',
        defaultMessage: 'Relay Output',
        description: 'Relay Output',
      }),
      run: formatMessage({
        id: 'sensor.control.runSignal',
        defaultMessage: 'Run Signal',
        description: 'Run Signal',
      }),
      pressure: formatMessage({
        id: 'sensor.pressure',
        defaultMessage: 'Pressure',
        description: 'Pressure',
      }),
    }
  )
  if (rows.length < 1) {
    return (
      <NoChartData />
    )
  }

  const columns = makeColumns([
    {
      type: 'date',
      label: formatMessage({
        id: 'sensor.time.short',
        defaultMessage: 'Time',
        description: 'Time',
      }),
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.control.relayOutput',
        defaultMessage: 'Relay Output',
        description: 'Relay Output',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.control.runSignal',
        defaultMessage: 'Run Signal',
        description: 'Run Signal',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.pressure',
        defaultMessage: 'Pressure',
        description: 'Pressure',
      }),
      role: 'data',
    },
  ])
  const dimensions = chartDimensions(window.innerWidth)

  const maxSignal = 2
  const minSignal = 0

  let maxPressure = 300
  const { physical } = device
  try {
    maxPressure = _maxBy(sensorData, o => o['133'])['133'] + 5
    maxPressure = fourToTwenty(
      maxPressure,
      physical.min || 0,
      physical.max || 100,
      physical.zero || 0,
      1
    )
  } catch (error) {
    console.error(error)
    return (
      <div className='deviceInfo'>
        Chart data not available
      </div>
    )
  }

  const options = {
    title: 'Pump',
    hAxis: {
      slantedText: false,
      gridlines: {
        count: -1,
        units: {
          days: { format: ['M/d'] },
          hours: { format: ['M/d ha'] },
        },
      },
      minorGridlines: {
        units: {
          hours: { format: ['ha'] },
          minutes: { format: ['HH:mm a Z', ':mm'] },
        },
      },
    },
    series: {
      0: {
        targetAxisIndex: 1,
        color: '#D00000',
        lineWidth: 4,
        areaOpacity: 1.0,
      },
      1: {
        targetAxisIndex: 1,
        color: '#23CE6B',
        lineWidth: 4,
        areaOpacity: 1.0,
      },
      2: {
        targetAxisIndex: 0,
        color: '#50b0ff',
        lineWidth: 4,
        areaOpacity: 0.0,
      },
    },
    vAxes: {
      // Adds titles to each axis.
      0: {
        title: formatMessage({
          id: 'sensor.pressure',
          defaultMessage: 'Pressure',
          description: 'Pressure',
        }),
        minValue: 0,
        maxValue: maxPressure,
        textPosition: 'in',
        gridlines: {
          count: -1,
        },
      },
      1: {
        title: formatMessage({
          id: 'sensor.control.relayOutput',
          defaultMessage: 'Relay Output',
          description: 'Relay Output',
        }),
        minValue: minSignal,
        maxValue: maxSignal,
        textPosition: 'none',
        gridlines: {
          count: 2,
        },
      },
    },
    chartArea: {
      left: 0,
      top: 0,
      width: '100%',
      height: '85%',
    },
    isStacked: false,
    legend: { position: 'bottom' },
    // This line makes the entire category's tooltip active.
    focusTarget: 'category',
    // Use an HTML tooltip.
    tooltip: {
      isHtml: true,
      trigger: 'focus',
    },
  }

  return (
    <div className={styles.chart}>
      <Chart
        chartType='AreaChart'
        rows={rows}
        width={dimensions.width}
        height={dimensions.height}
        columns={columns}
        options={options}
        style={{
          margin: '0 auto',
          position: 'relative',
          top: '5px',
        }}
      />
    </div>
  )
};
ChartPump.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  metricReady: PropTypes.bool,
  device: PropTypes.object,
}

export default injectIntl(ChartPump)
